import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import DisplayRecording from "./DisplayRecording";
import Alert from "../../common/components/Alert";
import Button from "../../common/components/Button";
import LoadingSpinner from "../../common/components/LoadingSpinner";

const VideoRecorder = ({
  isPreparing,
  onRecordingBegin,
  onRecordingComplete,
  onError,
  answerTime,
  onReset,
  stream,
  managerRef,
}) => {
  // State management
  const [recordingState, setRecordingState] = useState({
    isInitializing: false,
    error: null,
    displayCompletedVideo: false,
    recordingDuration: answerTime,
    posterImage: null,
    isRecording: false,
  });

  // Refs
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const isStartingRecording = useRef(false);

  // Recorded data
  const [videoBlob, setVideoBlob] = useState(null);

  // Cleanup function
  const cleanupResources = useCallback(async () => {
    console.trace("Cleaning up resources");
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Clear video source
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Cleanup manager
    if (managerRef.current) {
      await managerRef.current.cleanup();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle recording timer
  const startRecordingTimer = useCallback(() => {
    if (timerRef.current) return; // Prevent multiple timers

    timerRef.current = setInterval(() => {
      setRecordingState((prev) => ({
        ...prev,
        recordingDuration: prev.recordingDuration - 1,
      }));
    }, 1000);
  }, []);

  // Handle start recording
  const handleStartRecording = useCallback(async () => {
    console.log("Handle start recording called");
    if (isStartingRecording.current) {
      console.log("Recording is already starting");
      return;
    }

    if (!managerRef.current) {
      console.log("Manager not initialized");
      return;
    }

    try {
      isStartingRecording.current = true;
      onRecordingBegin && onRecordingBegin();

      await managerRef.current.startRecording();
      startRecordingTimer();

      setRecordingState((prev) => ({
        ...prev,
        isRecording: true,
        error: null,
      }));
    } catch (error) {
      console.error("Failed to start recording:", error);
      setRecordingState((prev) => ({
        ...prev,
        error: "Failed to start recording: " + error.message,
        isRecording: false,
      }));
      onError && onError(error);
    } finally {
      isStartingRecording.current = false;
    }
  }, [startRecordingTimer, onError, onRecordingBegin, managerRef]);

  // Set the video source to the stream once modals are closed and stream is available
  useEffect(() => {
    if (!videoRef.current || !stream) {
      return;
    }

    try {
      console.log("Setting video source to stream:", stream);
      videoRef.current.srcObject = stream;
    } catch (error) {
      console.error("Failed to set video source:", error);
      setRecordingState((prev) => ({
        ...prev,
        error: "Failed to set video source: " + error.message,
      }));
    }
  }, [stream]);

  //Reset recording state when video is uploaded
  const resetVideoRecorder = useCallback(async () => {
    console.log("Resetting video recorder");
    try {
      await cleanupResources();

      setRecordingState({
        isInitializing: false,
        error: null,
        displayCompletedVideo: false,
        recordingDuration: answerTime,
        posterImage: null,
        isRecording: false,
      });
      setVideoBlob(null);
    } catch (error) {
      console.error("Failed to reset video recorder:", error);
    }
  }, [answerTime, cleanupResources]);

  // Handle stop recording
  const handleStopRecording = useCallback(async () => {
    if (!recordingState.isRecording) {
      console.log("Not currently recording");
      return;
    }

    try {
      // Stop the timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Capture the frame before stopping
      const posterDataUrl = await managerRef.current.capturePoster(videoRef);
      const recordedBlob = await managerRef.current.stopRecording();

      // Ensure we have a proper video blob with type
      const videoBlob = new Blob([recordedBlob], {
        type: recordedBlob.type || "video/webm",
      });

      setVideoBlob(videoBlob);
      setRecordingState((prev) => ({
        ...prev,
        displayCompletedVideo: true,
        recordingDuration: answerTime,
        posterImage: posterDataUrl,
        error: null,
        isRecording: false,
      }));
      // Call the callback function from parent component when recording is complete
      onRecordingComplete && onRecordingComplete(videoBlob);
    } catch (error) {
      console.error("Failed to stop recording:", error);
      setRecordingState((prev) => ({
        ...prev,
        error: "Failed to save recording. Please try again.",
        isRecording: false,
      }));
      // Reset video recorder so user can try again
      await resetVideoRecorder();
    }
  }, [
    answerTime,
    onRecordingComplete,
    resetVideoRecorder,
    recordingState.isRecording,
    managerRef,
  ]);

  //Auto-stop recording when answer time is over
  useEffect(() => {
    if (recordingState.recordingDuration <= 0) {
      handleStopRecording();
    }
  }, [recordingState.recordingDuration, handleStopRecording]);

  //Reset recording state when submit is clicked in parent component
  useEffect(() => {
    if (onReset) {
      onReset(resetVideoRecorder);
    }
  }, [onReset, resetVideoRecorder]);

  // Handle refresh during recording
  useEffect(() => {
    const handleBeforeUnload = async (e) => {
      if (recordingState.isRecording) {
        e.preventDefault();
        e.returnValue = "";

        // Store error state in sessionStorage
        sessionStorage.setItem(
          "recordingError",
          JSON.stringify({
            message: "Video data lost due to page refresh",
            timestamp: Date.now(),
          })
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, [recordingState.isRecording]);

  // Check for persisted error on mount
  useEffect(() => {
    const persistedError = sessionStorage.getItem("recordingError");
    if (persistedError) {
      try {
        const error = JSON.parse(persistedError);
        onError && onError(new Error(error.message));
        // Clear the error from storage
        sessionStorage.removeItem("recordingError");
      } catch (error) {
        console.error("Error parsing persisted error:", error);
      }
    }
  }, [onError]);

  // Format recording duration
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Render recording controls
  const renderRecordingControls = () => (
    <div className="absolute bottom-4 left-0 right-0 grid grid-cols-3 items-center w-full">
      <div className="col-start-2 flex justify-center">
        {recordingState.isRecording ? (
          <div className="flex items-center">
            <button
              onClick={() =>
                !isStartingRecording.current && handleStopRecording()
              }
              disabled={
                recordingState.isInitializing ||
                isStartingRecording.current ||
                recordingState.recordingDuration <= 0 ||
                recordingState.recordingDuration > answerTime - 3
              }
              className={`
            w-10 h-10
            rounded-none bg-red-600 hover:bg-red-700
            ${
              recordingState.isInitializing || isStartingRecording.current
                ? "opacity-50 cursor-not-allowed"
                : ""
            }
            focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 
            transition-all duration-300
          `}
              aria-label="Stop Recording"
            />
            <div className="text-white font-mono pl-4">
              {formatDuration(recordingState.recordingDuration)}
            </div>
          </div>
        ) : (
          <Button
            onClick={() =>
              !isStartingRecording.current && handleStartRecording()
            }
            disabled={
              recordingState.isInitializing || isStartingRecording.current
            }
            text="Start Recording"
            className={`
            ${
              recordingState.isInitializing || isStartingRecording.current
                ? "opacity-50 cursor-not-allowed"
                : ""
            }
          `}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Error Alert */}
      <Alert
        title="Error"
        message={recordingState.error}
        colorClass="error"
        showAlert={!!recordingState.error}
        setShowAlert={() =>
          setRecordingState((prev) => ({ ...prev, error: null }))
        }
      />

      {/* Main Content */}
      {recordingState.displayCompletedVideo ? (
        <DisplayRecording
          videoBlob={videoBlob}
          posterImage={recordingState.posterImage}
          onRetry={() => {
            setRecordingState({
              isInitializing: false,
              error: null,
              displayCompletedVideo: false,
              recordingDuration: 0,
            });
            setVideoBlob(null);
          }}
        />
      ) : (
        <div className="relative w-[640px] h-[480px] bg-black rounded-lg overflow-hidden">
          {/* Loading State */}
          {recordingState.isInitializing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <LoadingSpinner />
            </div>
          )}
          {/* Video RecordingView */}
          <video
            ref={videoRef}
            className="w-full h-full object-cover [transform:scaleX(-1)]"
            autoPlay
            muted
            playsInline
          />
          {/* Recording Controls */}
          {renderRecordingControls()}
        </div>
      )}
    </div>
  );
};

VideoRecorder.propTypes = {
  isPreparing: PropTypes.bool,
  onRecordingBegin: PropTypes.func,
  onRecordingComplete: PropTypes.func,
  onError: PropTypes.func,
  answerTime: PropTypes.number,
  onReset: PropTypes.func,
  stream: PropTypes.object,
  managerRef: PropTypes.object,
};

export default React.memo(VideoRecorder);
